import { legalEntity } from '../../legal';
import type { Translation } from './index';

export const de: Translation = {
  meta: {
    title: 'Denqfabrik — Schnittstellen zwischen Shop, ERP und Marktplatz',
    description:
      'Denqfabrik baut, testet und betreibt Schnittstellen für Handels- und E-Commerce-Teams: Bestellungen, Bestände und Preise laufen automatisch zwischen Shop, ERP und Marktplatz.',
    ogTitle:
      'Denqfabrik — Schnittstellen zwischen Shop, ERP und Marktplatz. Gebaut, getestet, betrieben.',
    ogDescription:
      'Bestellungen, Bestände und Preise laufen automatisch zwischen Ihren Systemen — WooCommerce, Shopware, JTL, Amazon, Mirakl und mehr. Kein manuelles Nachpflegen.',
  },
  nav: {
    home: 'Home',
    agents: 'KI-Agenten',
    useCases: 'Use Cases',
    solutions: 'Solutions',
    integrations: 'Integrationen',
    configurator: 'Konfigurator',
    // Label only — the route stays /de/pricing (19 live Google Ads point at that URL).
    pricing: 'Preise',
    amazonSpApi: 'Amazon SP-API',
    about: 'Über uns',
    contact: 'Kontakt',
    services: 'Use Cases',
    safety: 'Sicherheit',
    safetyLabel: 'Sichere KI-Agenten bauen',
    safeAgents: 'Sichere KI-Agenten',
    process: 'Prozess',
    blog: 'Blog',
    cta: 'Gespräch starten',
    languageSwitch: 'EN',
    resources: 'Ressourcen',
    resourcesSafetyDesc: 'Leitfaden für robuste AI-Systeme',
    resourcesProjectsDesc: 'Echte Projekte mit echten Ergebnissen',
    resourcesIntegrationsDesc: 'ERP, Marktplätze & Tools verbinden',
    resourcesBlogDesc: 'Alle Beiträge ansehen',
    resourcesAboutDesc: 'Team & Mission',
    resourcesLatestPosts: 'Neueste Beiträge',
  },
  hero: {
    badge: 'Für Handels- und E-Commerce-Teams im DACH-Raum',
    headlineLine1: 'Schnittstellen zwischen Shop, ERP und Marktplatz.',
    headlineLine2: 'Gebaut, getestet, betrieben.',
    subheadline:
      'Bestellungen, Bestände und Preise laufen automatisch zwischen Ihren Systemen — WooCommerce, Shopware, JTL, Amazon, Mirakl und mehr. Kein manuelles Nachpflegen, kein Baukasten.',
    ctaPrimary: 'Schnittstelle konfigurieren',
    ctaSecondary: 'Projekt anfragen',
    flow: {
      sourcesLabel: 'Ihre Systeme',
      targetsLabel: 'Kanäle & Ziele',
      hubTitle: 'Denqfabrik Sync',
      hubItems: ['Bestellungen', 'Bestände', 'Preise'],
      hubNote: 'Vorab getestet, laufend überwacht, robust bei Störung.',
    },
  },
  trust: {
    subtitle: 'Commerce-Unternehmen vertrauen auf',
    headingPrefix: 'Gebaut mit',
    ctaButton: 'Kundenstimmen ansehen',
    title: 'Vertrauen von Commerce-Unternehmen im DACH-Raum',
    description:
      'Denqfabrik liefert keine Demo-Slides, sondern produktive Systeme in realen Handelsprozessen.',
    logoWallTitle: 'Ausgewählte Unternehmen',
    testimonials: [
      {
        quote:
          'Durch die starke Saisonalität unseres Geschäfts brauchen wir stabile Prozesse. René hat entscheidende Abläufe automatisiert, sodass wir auch in der Hochsaison reibungslos verkaufen können.',
        author: 'Selma Kirsch',
        role: 'CEO',
        company: 'Alpenmärchen GmbH',
      },
      {
        quote:
          'Wir betreiben Dropshipping. Die Denqfabrik hat in Plentymarkets die Partnerlager-Schnittstellen gebaut und damit die Auftragsabwicklung vollständig automatisiert.',
        author: 'Fiona Grubits',
        role: 'CEO',
        company: 'Playflip',
      },
      {
        quote:
          'Schnittstellen leben und verändern sich. Oft werden wir nicht informiert und Prozesse brechen plötzlich. Es ist uns wichtig, dass wir uns auf René verlassen können, wenn es brennt.',
        author: 'Waldemar Derksen',
        role: 'CEO',
        company: 'Albatros International GmbH',
      },
    ],
  },
  services: {
    label: 'USE CASES',
    title: 'Typische Agenten, die wir bauen',
    description:
      'Klare Einsatzbilder statt "KI für alles": Diese Agenten-Typen setzen wir regelmäßig in Commerce-Prozessen um.',
    cards: [
      {
        title: 'Support & Case Handling Agent',
        description: 'Standardfälle im Kundenservice automatisch bearbeiten und sauber eskalieren.',
        points: [
          'E-Mail-Klassifikation und Routing',
          'Storno-, Retoure- und Gutschriften-Standardfälle',
          'Kontextzugriff auf ERP, Shop und Logistik',
          'Human-in-the-Loop bei Unsicherheit',
        ],
      },
      {
        title: 'Marketplace Operations Agent',
        description:
          'Marktplatzprozesse überwachen, auffällige Fälle erkennen und Vorschläge liefern.',
        points: [
          'Bestands- und Preis-Monitoring mit Vorschlägen',
          'Vendor-Prozesse und Sonderfälle steuern',
          'Fehlgeschlagene Flows frühzeitig erkennen',
          'Gezielte Eskalation bei Risiken',
        ],
      },
      {
        title: 'Content & Produktdaten Agent',
        description: 'Produktdaten strukturiert erzeugen und vor Veröffentlichung validieren.',
        points: [
          'Strukturierte HTML-Beschreibungen generieren',
          'Bild- und Kontext-basierte Texterstellung',
          'Validierung von Style, Compliance und Format',
          'Freigabefähige Entwürfe für Teams',
        ],
      },
      {
        title: 'Finance & Reconciliation Agent',
        description: 'Abgleiche, Gebührenprüfung und Abweichungen transparent aufbereiten.',
        points: [
          'Zahlungsabgleich und Gebührenprüfung',
          'Auffälligkeiten und Differenzen markieren',
          'Reporting für Finance-Teams',
          'Vorbereitung für Buchhaltungsprozesse',
        ],
      },
    ],
  },
  safety: {
    title: 'Enterprise-KI-Agenten, denen Sie vertrauen können.',
    description: 'Ein KI-Agent ohne Sicherheitsarchitektur ist kein Asset, sondern ein Risiko.',
    statement:
      'Wir planen Fehlermodi, Leitplanken und Eskalationspfade vor der ersten Produktivaktion.',
    principles: [
      {
        title: 'Agenten müssen wissen, was sie nicht wissen',
        description:
          'Out-of-Distribution-Erkennung und Abstinenzstrategien stoppen Aktionen bei Unsicherheit, statt zu halluzinieren.',
      },
      {
        title: 'Jede Entscheidung braucht einen Audit-Trail',
        description:
          'Inputs, Kontext, Entscheidung, Guardrails und Ergebnis werden lückenlos dokumentiert.',
      },
      {
        title: 'Leitplanken sind architektonisch, nicht kosmetisch',
        description:
          'Policies, Grenzen, Freigaben und Fallbacks sind Teil des Kerndesigns, nicht ein nachträglicher Patch.',
      },
      {
        title: 'Fehlermodi werden designed, nicht entdeckt',
        description:
          'Circuit Breaker, Graceful Degradation und Human-Eskalation sind vorab spezifiziert.',
      },
    ],
    metrics: [],
  },
  process: {
    title: 'Vom Problem zum produktionssicheren Agenten.',
    description:
      'Projektarbeit in vier Schritten: klarer Scope, Sicherheitsdesign im Kern und stabiler Betrieb nach Go-live.',
    steps: [
      {
        title: 'Discovery',
        description:
          'Commerce-Prozesse kartieren, Potenziale identifizieren und Sicherheitsgrenzen definieren. Kein Build ohne klare Fehlermodi.',
      },
      {
        title: 'Architektur',
        description:
          'Agentendesign mit Leitplanken, Entscheidungsgrenzen und Eskalationspfaden. Sicherheitsschicht vor Feature-Schicht.',
      },
      {
        title: 'Build & Verify',
        description:
          'Iterative Entwicklung mit Verifikation gegen Testsuiten und Edge Cases, bevor Automatisierung produktiv geht.',
      },
      {
        title: 'Deploy & Monitor',
        description:
          'Deployment mit Monitoring, Anomalieerkennung und Circuit Breakern. Betrieb und Verbesserung bleiben im Loop.',
      },
    ],
  },
  integration: {
    title: 'Ihre Systeme. Sicher verbunden.',
    highlight: 'MCP-Engineering aus der Praxis',
    description:
      'Wir bauen keine Demos — wir bauen MCP-Server, API-Pipelines und Echtzeit-Schnittstellen, die in Ihrer Produktivumgebung laufen.',
    systems: [
      'Shopify / WooCommerce / Shopware',
      'Plentymarkets',
      'Marktplätze (OTTO, Mirakl, Amazon u. a.)',
      'ERP-Systeme (z. B. SAP, WeClapp, individuelle ERPs)',
      'Fulfillment- und Logistiksysteme',
    ],
    note: 'Detaillierte Integrations- und Tool-Listen gehören auf die Solutions-Seiten. Amazon bleibt ein eigener Sonderfall.',
    platforms: [
      'SAP',
      'plentymarkets',
      'Xentral',
      'Odoo',
      'Dynamics 365',
      'Sage',
      'Shopify',
      'Shopware',
      'WooCommerce',
      'Magento',
      'BigCommerce',
      'PrestaShop',
      'Amazon',
      'OTTO',
      'Kaufland',
      'eBay',
      'Zalando',
      'Allegro',
      'Etsy',
      'Temu',
      'ManoMano',
      'Walmart',
      'Galaxus',
      'Klaviyo',
      'HubSpot',
      'Salesforce',
      'Zendesk',
      'Brevo',
      'DHL',
      'FedEx',
      'UPS',
    ],
    featured: ['Amazon', 'Shopify', 'plentymarkets'],
    featuredCards: [
      {
        title: 'Amazon & Marktplätze',
        description:
          'SP-API, OTTO, eBay, Kaufland, Mirakl — automatisierte Auftragsabwicklung, Listing-Management und EDI-Compliance.',
        cta: 'Amazon SP-API entdecken',
        href: '/integrationen/amazon-sp-api',
      },
      {
        title: 'Shopware & Shopify',
        description:
          'Order-Management, Retouren-Handling und ERP-Synchronisation für moderne Commerce-Stacks.',
        cta: 'Integrationen ansehen',
        href: '/integrationen',
      },
      {
        title: 'Individuelle Systeme',
        description:
          'Custom ERPs, Legacy-Schnittstellen, Eigenentwicklungen — wir docken an, egal was Sie betreiben.',
        cta: 'Use Cases ansehen',
        href: '/use-cases',
      },
    ],
    hoverPrefix: 'MCP-Server für',
    ctaButton: 'Use Cases entdecken',
    finalCard: {
      title: 'Agent Hub Orchestrierung',
      subtitle: 'ERP, Shop, Marketplace und CRM in einem sicheren Runtime-Flow verbunden.',
      chips: ['Policy Layer', 'Audit Trail', 'Failover Ready'],
    },
  },
  projects: {
    title: 'Ausgewählte Projekte',
    description: 'Kurzes Format: Problem -> Lösung -> Ergebnis.',
    labels: {
      problem: 'Problem',
      solution: 'Lösung',
      result: 'Ergebnis',
    },
    cases: [
      {
        name: 'Fuchsversand24 — Produktdaten-Automatisierung',
        problem: 'Hoher manueller Aufwand für konsistente Produktbeschreibungen.',
        solution:
          'Agent zur Generierung strukturierter HTML-Beschreibungen mit Kontext und Validierung vor Veröffentlichung.',
        result: 'Schnellere Content-Erstellung und konsistentere Darstellung im Katalog.',
      },
      {
        name: 'Playflip — Dropshipping-Automation',
        problem: 'Auftragsabwicklung über Partnerlager erfordert fehlerfreie Koordination.',
        solution:
          'Partnerlager-Schnittstellen in Plentymarkets gebaut und zentrale Workflows automatisiert.',
        result: 'Vollautomatisierte Auftragsabwicklung.',
      },
      {
        name: 'Alpenmärchen — Saisonstabile Prozesse',
        problem: 'Lastspitzen durch Saisonalität erzeugen operativen Prozessstress.',
        solution: 'Kritische Abläufe automatisiert und auf Peak-Phasen ausgelegt.',
        result: 'Reibungsloser Verkauf auch in Hochsaisonzeiten.',
      },
    ],
  },
  agentsTeaser: {
    label: 'Der Schritt danach',
    text: 'Wenn die Schnittstelle steht, ist der nächste Schritt ein Agent, der auf ihr entscheidet.',
    link: 'KI-Agenten ansehen',
  },
  cta: {
    headline: 'Wer jetzt automatisiert,\nführt morgen.',
    headlineHighlight: 'Denqfabrik',
    button: 'Gespräch starten',
    card: {
      title: 'Wer steckt hinter Denqfabrik?',
      description:
        'Ein kleines, spezialisiertes Team mit direktem Draht — kein Agentur-Karussell, sondern feste Ansprechpartner, die Ihr System kennen.',
      link: 'Team kennenlernen',
    },
  },
  footer: {
    copyright: `© 2026 ${legalEntity.name}`,
    impressum: 'Impressum',
    datenschutz: 'Datenschutz',
    linkedin: 'LinkedIn',
    github: 'GitHub',
  },
  structuredData: {
    description:
      'Denqfabrik baut, testet und betreibt Schnittstellen zwischen Shop, ERP und Marktplatz für Handels- und E-Commerce-Teams — sowie KI-Agenten mit Sicherheitsarchitektur, Leitplanken und Audit-Trails.',
    offerDescription:
      'Schnittstellen zwischen Shop, ERP und Marktplatz inklusive Anbindung, Test, Monitoring und Betrieb.',
  },
  about: {
    hero: {
      label: 'Über Denqfabrik',
      headline: 'Commerce-Automation',
      headlineHighlight: 'mit technischer Tiefe.',
      subtitle:
        'Kein Agentur-Karussell, sondern feste Ansprechpartner, die Ihr System kennen, bauen und betreiben.',
    },
    founder: {
      label: 'Gründer & technische Leitung',
      name: 'René Nowotny',
      role: 'Gründer & technische Leitung',
      bio1: 'Drei Jahre CTO in einer E-Commerce-Holding — verantwortlich für die technische Integration zugekaufter Unternehmen, von ERP-Anbindungen bis zu Marktplatz-Rollouts.',
      bio2: 'Seit vier Jahren baut er mit Denqfabrik Integrationen und Automationen für Commerce-Teams — von der Einzelanbindung bis zur KI-gestützten Prozessautomatisierung.',
      stats: [
        { value: '7+', label: 'Jahre E-Commerce' },
        { value: '3', label: 'Jahre CTO' },
        { value: '50+', label: 'Integrationen gebaut' },
        { value: '20+', label: 'Plattformen' },
      ],
    },
    partners: {
      label: 'Entwicklungspartner',
      description:
        'Spezialisierte Engineers, die fest mit Denqfabrik an Commerce-Automation arbeiten.',
      list: [
        {
          name: 'Alexander Geils',
          role: 'AI Software Engineer',
          focus:
            'KI-gestützte Agenten, LLM-Integration, Daten-Pipelines, produktionsreife Agent-Architekturen.',
          bio: 'Alex bringt Erfahrung aus dem Aufbau AI-gestützter SaaS-Produkte sowie der Automatisierung komplexer Geschäftsprozesse mit. Sowohl in Startups als auch im Konzernumfeld entwickelt er praxisnahe digitale Lösungen, die operative Abläufe effizienter machen, Prozesse vereinfachen und Teams spürbar entlasten.',
        },
        {
          name: 'Moritz Hawener',
          role: 'AI Software Engineer',
          focus:
            'MCP-Server, API-Schnittstellen, Echtzeit-Konnektoren, Systemintegration, Testing & Deployment.',
          bio: 'Als Founding Engineer und AI-Spezialist schlägt Moritz die Brücke zwischen komplexem Engineering und messbarem Geschäftsnutzen. Mit Erfahrung aus dem Aufbau agentischer Pipelines und der Beratung von Führungsebenen zur AI-Adoption entwickelt er Lösungen, die Prozesse radikal vereinfachen und Teams effizienter machen.',
        },
      ],
    },
    principles: {
      label: 'Arbeitsweise',
      title: 'So arbeiten wir.',
      list: [
        {
          title: 'Direkter Draht, keine Agentur-Schleife',
          description:
            'Sie sprechen immer mit den Menschen, die Ihr System auch tatsächlich bauen und betreiben.',
        },
        {
          title: 'Betriebssicherheit vor Feature-Showcase',
          description:
            'Klare Scope-Definition, klare Leitplanken, klare Verantwortung — bevor eine Zeile Code in Produktion geht.',
        },
        {
          title: 'Commerce-Fokus, keine Generalisten',
          description:
            'Wir arbeiten ausschließlich an Prozessen mit direktem operativem Hebel im Commerce-Betrieb.',
        },
      ],
    },
    platform: {
      label: 'Integrations-Erfahrung',
      title: '50+ Integrationen gebaut.',
      titleFaded: '20+ Plattformen.',
      description:
        'Von ERP über Shop-Systeme bis zu Marktplätzen — wir haben produktive Schnittstellen zu diesen Systemen gebaut und betreiben sie.',
      platforms: [
        'plentymarkets',
        'Shopify',
        'Shopware',
        'WooCommerce',
        'Amazon SP-API',
        'eBay',
        'OTTO',
        'Kaufland',
        'Mirakl',
        'JTL',
        'Billbee',
        'Xentral',
        'weclapp',
        'Odoo',
        'Afterbuy',
        'ITscope',
        'Lexware',
        'CHECK24',
        'idealo',
        'HubSpot',
        'Pipedrive',
        'Custom ERPs',
      ],
    },
    cta: {
      title: 'Lassen Sie uns über Ihren Prozess sprechen.',
      description:
        'Im Erstgespräch klären wir, welche Automationsstufe sinnvoll ist und wie wir den Weg dorthin absichern.',
      button: 'Erstgespräch vereinbaren',
    },
    meta: {
      title: 'Über uns',
      description:
        'Denqfabrik baut sichere Commerce-Automation mit technischer Tiefe und klarer operativer Verantwortung.',
    },
  },
  contact: {
    label: 'Kontakt',
    titleDefault: 'Lassen Sie uns sprechen',
    titleSelected: 'Fast geschafft!',
    subtitleDefault:
      'Bereit, Ihre E-Commerce-Prozesse zu optimieren? Kontaktieren Sie uns für ein unverbindliches Erstgespräch.',
    subtitleSelected: 'Nur noch wenige Angaben und wir melden uns innerhalb von 24 Stunden.',
    callBooking: {
      title: '30-Minuten Call buchen',
      subtitle: 'Kostenlos & unverbindlich',
      description:
        'Direkt Termin sichern. Kein Sales-Gespräch, sondern ehrliche Beratung zu Ihrem Projekt.',
      button: 'Termin auswählen',
    },
    info: {
      phone: { title: 'Telefon', value: legalEntity.phone },
      email: { title: 'E-Mail', value: legalEntity.email },
      location: { title: 'Standort', value: 'Seelze bei Hannover, Deutschland' },
    },
    availability: {
      title: 'Telefonisch erreichbar',
      days: 'Montag – Sonntag',
      hours: '08:00 – 12:00 & 14:00 – 18:00',
    },
    responseTime: 'Antwort innerhalb von 24 Stunden',
    form: {
      nameLabel: 'Name *',
      namePlaceholder: 'Ihr Name',
      emailLabel: 'E-Mail *',
      emailPlaceholder: 'ihre@email.de',
      companyLabel: 'Unternehmen',
      companyPlaceholder: 'Ihr Unternehmen',
      phoneLabel: 'Telefon',
      phonePlaceholder: '+49 157 890 952 76',
      systemsLabel: 'Welche Systeme nutzen Sie? (optional)',
      systemsPlaceholder: 'z.B. Shopify, Plentymarkets, Amazon, ...',
      messageLabel: 'Nachricht *',
      messageLabelSelected: 'Kurze Beschreibung *',
      messagePlaceholder: 'Erzählen Sie uns von Ihrem Projekt und Ihren Anforderungen...',
      messagePlaceholderSelected: 'Was möchten Sie verbessern oder automatisieren?',
      submitDefault: 'Nachricht senden',
      submitSelected: 'Anfrage absenden',
      submitting: 'Wird gesendet...',
    },
    selectedOption: {
      interestedIn: 'Sie interessieren sich für:',
      changeSelection: 'Auswahl ändern',
      noSelection: 'Keine Auswahl',
      prefillMessage: 'Interesse an: {option}\n\nKurze Beschreibung meines Vorhabens:\n',
    },
    status: {
      success: 'Vielen Dank! Ihre Nachricht wurde erfolgreich gesendet.',
      error: 'Es ist ein Fehler aufgetreten. Bitte versuchen Sie es später erneut.',
    },
    validation: {
      nameMin: 'Name muss mindestens 2 Zeichen lang sein',
      emailInvalid: 'Bitte geben Sie eine gültige E-Mail-Adresse ein',
      messageMin: 'Nachricht muss mindestens 10 Zeichen lang sein',
    },
  },
  thankYou: {
    meta: {
      title: 'Vielen Dank für Ihre Anfrage',
    },
    title: 'Vielen Dank — wir haben Ihre Nachricht erhalten.',
    subtitle:
      'Wir melden uns innerhalb von 24 Stunden bei Ihnen, meist deutlich schneller. Sie erhalten parallel eine Bestätigung per E-Mail.',
    nextStep: {
      heading: 'Direkt einen 30-Minuten Call sichern',
      description:
        'Wenn es passt: buchen Sie sich gleich einen unverbindlichen Termin. Kein Sales-Gespräch, sondern eine ehrliche Einschätzung zu Ihrem Projekt.',
      cta: 'Termin auswählen',
    },
    backHome: 'Zurück zur Startseite',
  },
  bookingConfirmed: {
    meta: {
      title: 'Termin bestätigt — Denqfabrik',
    },
    title: 'Ihr Termin ist gebucht.',
    subtitle:
      'Wir haben Ihre Terminanfrage erhalten. Eine Bestätigung mit Kalender-Einladung und Zoom-Link liegt in Ihrem Postfach.',
    nextStep: {
      heading: 'Was als Nächstes passiert',
      description:
        'Sie erhalten kurz vor dem Termin eine Erinnerung. Falls etwas dazwischenkommt: einfach in der Bestätigungsmail verschieben oder absagen — kein Stress.',
    },
    backHome: 'Zurück zur Startseite',
  },
  notFound: {
    title: 'Seite nicht gefunden',
    description:
      'Die angeforderte Seite existiert leider nicht. Möglicherweise wurde sie verschoben oder gelöscht.',
    button: 'Zur Startseite',
    meta: {
      title: 'Seite nicht gefunden',
    },
  },
  imprint: {
    meta: {
      title: 'Impressum',
      description:
        'Impressum von Denqfabrik: Anbieterkennzeichnung, Registereintrag und Kontaktdaten.',
    },
  },
  privacy: {
    meta: {
      title: 'Datenschutzerklärung',
      description:
        'Datenschutzerklärung von Denqfabrik: welche personenbezogenen Daten diese Website verarbeitet, wozu und welche Rechte Sie haben.',
    },
  },
  blog: {
    meta: {
      title: 'Blog',
      description: 'Einblicke in E-Commerce-Automatisierung, Systemintegrationen und KI-Agenten.',
    },
  },
  kiAgenten: {
    meta: {
      title: 'Anwendungsfälle für KI-Agenten',
      description:
        'KI-Agenten automatisieren E-Mails, Stornierungen, Lieferstatus und Speditionskommunikation im E-Commerce.',
    },
    hero: {
      label: 'Anwendungsfälle',
      titleLine1: 'Ihr Team beantwortet E-Mails.',
      titleLine2: 'Unser Agent auch.',
      subtitle:
        'Schneller, konsistenter, dokumentiert — mit Kontext aus allen Systemen und klaren Eskalationspfaden.',
    },
    simulation: {
      label: 'Live-Simulation',
      title: 'Eine E-Mail kommt rein. Der Agent übernimmt.',
      description:
        'Klicken Sie auf ein Szenario und sehen Sie, wie der Agent Kontext aus mehreren Systemen zieht, prüft und handelt.',
      fromTypeLabels: { kunde: 'Kunde', spedition: 'Spedition', intern: 'Intern' },
      agentProcessing: 'Agent-Verarbeitung',
      resultLabels: { systems: 'Systeme', duration: 'Dauer' },
      fromPrefix: 'Von',
    },
    scenarios: [
      {
        id: 'stornierung',
        seoTitle: 'Bestellungen automatisch stornieren',
        from: 'Anna Müller',
        fromType: 'kunde',
        subject: 'Kann ich meine Bestellung noch stornieren?',
        preview:
          'Hallo, ich habe gerade Bestellung #38291 aufgegeben, aber brauche den Artikel doch nicht. Kann ich noch stornieren bevor es losgeschickt wird?',
        time: '09:14',
        steps: [
          {
            label: 'E-Mail klassifiziert',
            detail: 'Kategorie: Stornierung · Confidence: 0.96',
            status: 'success',
          },
          {
            label: 'Bestellung #38291 geladen',
            detail: 'Status: In Kommissionierung · Noch nicht versendet',
            status: 'info',
          },
          {
            label: 'Stornierung möglich',
            detail: 'Policy: Storno vor Versand erlaubt',
            status: 'success',
          },
          {
            label: 'Stornierung ausgeführt',
            detail: 'WMS: Picking gestoppt · ERP: Gutschrift erstellt',
            status: 'success',
          },
        ],
        result: {
          action: 'Bestätigung an Kundin gesendet, Bestellung storniert',
          system: 'Shop + WMS + ERP',
          duration: '< 30 Sekunden',
        },
      },
      {
        id: 'lieferstatus',
        seoTitle: 'Lieferstatus automatisch beantworten',
        from: 'Thomas Weber',
        fromType: 'kunde',
        subject: 'Wo ist meine Lieferung? Ich warte seit 5 Tagen.',
        preview:
          'Bestellnummer #49382. Laut Bestellbestätigung sollte es längst da sein. Können Sie mir sagen wo mein Paket ist?',
        time: '10:31',
        steps: [
          {
            label: 'E-Mail klassifiziert',
            detail: 'Kategorie: Lieferstatus · Confidence: 0.93',
            status: 'success',
          },
          {
            label: 'Bestellung #49382 geladen',
            detail: 'Versand: DHL · Tracking: 1234567890',
            status: 'info',
          },
          {
            label: 'Tracking abgefragt',
            detail: 'DHL Status: "In Zustellung" seit heute morgen',
            status: 'success',
          },
          {
            label: 'Automatische Antwort',
            detail: 'Tracking-Link + Statusupdate generiert',
            status: 'success',
          },
        ],
        result: {
          action: 'Tracking-Status mit Link an Kunden gesendet',
          system: 'Shop + DHL API',
          duration: '< 15 Sekunden',
        },
      },
      {
        id: 'spedition',
        seoTitle: 'Speditionskommunikation automatisieren',
        from: 'Schenker Logistics',
        fromType: 'spedition',
        subject: 'Lieferverzögerung: 3 Paletten +2 Tage',
        preview:
          'Sehr geehrte Damen und Herren, die Zustellung der Sendungen LKW-2847 verzögert sich um voraussichtlich 2 Werktage aufgrund von Kapazitätsengpässen.',
        time: '07:45',
        steps: [
          {
            label: 'E-Mail klassifiziert',
            detail: 'Kategorie: Lieferverzögerung · Absender: Spedition',
            status: 'warning',
          },
          {
            label: 'Sendung LKW-2847 aufgelöst',
            detail: '14 Bestellungen betroffen · 11 Kunden',
            status: 'info',
          },
          {
            label: 'SLA-Check',
            detail: '3 Bestellungen überschreiten Lieferversprechen',
            status: 'warning',
          },
          {
            label: 'Kunden proaktiv informiert',
            detail: '11 personalisierte E-Mails generiert und versendet',
            status: 'success',
          },
        ],
        result: {
          action: '11 Kunden proaktiv über Verzögerung informiert',
          system: 'WMS + Shop + E-Mail',
          duration: '< 2 Minuten',
        },
      },
      {
        id: 'gutschrift',
        seoTitle: 'Retouren und Gutschriften automatisch prüfen',
        from: 'Lisa (Buchhaltung)',
        fromType: 'intern',
        subject: 'Gutschrift für Retoure #8291 prüfen',
        preview:
          'Hi, kannst du mal checken ob die Gutschrift für Retoure #8291 korrekt ist? Der Kunde reklamiert, dass der Betrag nicht stimmt.',
        time: '11:22',
        steps: [
          {
            label: 'Anfrage klassifiziert',
            detail: 'Kategorie: Gutschriftprüfung · Intern',
            status: 'info',
          },
          {
            label: 'Retoure #8291 geladen',
            detail: 'Artikel zurück · Zustand: einwandfrei · Wert: 149,90 \u20AC',
            status: 'info',
          },
          {
            label: 'Abgleich ERP \u2194 Shop',
            detail: 'Gutschrift im ERP: 129,90 \u20AC · Differenz: 20,00 \u20AC (Versandkosten)',
            status: 'warning',
          },
          {
            label: 'Korrektur vorbereitet',
            detail: 'Versandkosten-Erstattung fehlt · Korrektur-Gutschrift erstellt',
            status: 'success',
          },
        ],
        result: {
          action: 'Differenz erkannt, Korrektur-Gutschrift an Buchhaltung übergeben',
          system: 'ERP + Shop',
          duration: '< 45 Sekunden',
        },
      },
    ],
    impact: {
      label: 'Vorher / Nachher',
      title: 'Was sich konkret ändert',
      description:
        'Jede Anfrage folgt dem gleichen Muster — aber Ihr Team macht es heute manuell. Der Agent übernimmt die Zuarbeit; Ihre Profis bleiben für Ausnahmen und Eskalationen zuständig.',
      flowLabel: 'Immer das gleiche Muster',
      flowSteps: ['E-Mail rein', 'Kontext laden', 'Regeln prüfen', 'Handeln oder eskalieren'],
      flowNote:
        'Egal ob Kunde, Spedition oder internes Team — der Agent folgt Ihren Regeln. Bei Unsicherheit eskaliert er an Ihr Team.',
      withoutAgent: {
        label: 'Ohne Agent',
        items: [
          {
            text: 'Mehrere Systeme öffnen, Daten zusammensuchen',
            metric: '10\u201315 Min. pro Fall',
          },
          {
            text: 'Jede E-Mail einzeln lesen, klassifizieren, zuordnen',
            metric: 'Fehleranfällig bei Volumen',
          },
          { text: 'Kunden warten auf Antwort', metric: '\u00D8 2\u20134 Stunden Reaktionszeit' },
          {
            text: 'Verzögerungen fallen erst auf, wenn Kunden sich melden',
            metric: 'Reaktiv statt proaktiv',
          },
        ],
      },
      withAgent: {
        label: 'Mit Agent',
        items: [
          {
            text: 'Kontext wird automatisch aus allen Systemen geladen',
            metric: 'Shop + ERP + WMS + Logistik',
          },
          {
            text: 'E-Mails werden in Sekunden klassifiziert und bearbeitet',
            metric: '< 30 Sek. bis zur Antwort',
          },
          {
            text: 'Kunden werden proaktiv informiert',
            metric: 'z.B. 11 Mails bei einer Lieferverzögerung',
          },
          {
            text: 'Eskalation bei Unsicherheit — Ihre Profis entscheiden',
            metric: 'Agent übernimmt nur die Zuarbeit',
          },
        ],
      },
      metrics: [
        { label: 'Bearbeitungszeit', before: '10\u201315 Min.', after: '< 30 Sek.' },
        { label: 'Reaktionszeit', before: '2\u20134 Stunden', after: 'Sofort' },
        { label: 'Systemzugriffe', before: '3\u20135 manuell', after: 'Automatisch' },
        { label: 'Eskalationen', before: 'Alles manuell', after: 'Nur Ausnahmen' },
      ],
    },
    cta: {
      title: 'Welche E-Mails beantwortet Ihr Team jeden Tag?',
      description:
        'Zeigen Sie uns 5 typische Anfragen — wir zeigen Ihnen, welche ein Agent übernehmen kann.',
      button: 'Erstgespräch vereinbaren',
    },
  },
  useCases: {
    meta: {
      title: 'Use Cases',
      description: 'Konkrete Einsatzfelder für Denqfabrik-Agenten im Commerce-Betrieb.',
    },
    hero: {
      label: 'Anwendungsszenarien',
      title: 'Use Cases',
      description: 'Konkrete Einsatzfelder für Denqfabrik-Agenten im Commerce-Betrieb.',
    },
    caseStudies: {
      label: 'Case Studies',
      title: 'Gebaut. Getestet.',
      titleFaded: 'Produktiv.',
      description:
        'Reale Systeme, die wir für Commerce-Unternehmen entwickelt und in Produktion gebracht haben.',
      detailsButton: 'Details ansehen',
      viewAllButton: 'Alle Use Cases ansehen',
      cards: [
        {
          slug: 'fulfillment-hub',
          kicker: 'Fulfillment',
          title: 'Zentrales Integrationshub für Fulfillment-Kunden',
          stat: '3h Onboarding',
          description:
            'Anbindung neuer Fulfillment-Kunden in Stunden statt Wochen. Zentrales Hub mit einheitlicher API, automatisiertem Onboarding-Prozess und Dashboard als Echtzeit-Übersicht für Bestände, Aufträge und SLA-Einhaltung.',
          tags: ['Fulfillment', 'Dashboard', 'Onboarding', 'API Hub'],
        },
        {
          slug: 'repricer',
          kicker: 'Pricing',
          title: 'Multi-Layer Repricer mit vier Datenschichten',
          stat: '4 Schichten',
          description:
            'Intelligentes Repricing-System das Fulfillment-Kapazitäten, produktbezogene Verkaufsdaten, interne Preisregeln und Konkurrenzpreise gleichzeitig berücksichtigt. Vollständig custom designed für maximale Marge bei Wettbewerbsfähigkeit.',
          tags: ['Repricer', 'Custom', 'Lagerkapazität', 'Konkurrenzanalyse'],
        },
        {
          slug: 'integration-hub',
          kicker: 'Integrationen',
          title: 'E-Commerce Integrationshub — Marktplätze an einem Ort',
          stat: 'Tage statt Monate',
          description:
            'Refunds, Orders, Stocks — alle Marktplätze in einem System. Unit Tests, E2E Tests und hohe Sicherheitsstandards. Neue Marktplätze in Tagen angebunden, noch bevor große Connector-Anbieter eine Schnittstelle bereitstellen.',
          tags: ['Marktplätze', 'E2E Tests', 'Early Access', 'Sicherheit'],
        },
        {
          slug: 'dropship-ai',
          kicker: 'Dropship + AI',
          title: 'AI-gestützte Dropshipping-Anbindung mit Übersetzung',
          stat: 'NL → DE automatisch',
          description:
            'Anbindung eines niederländischen Partners mit AI-Übersetzung der Produkttexte ins Deutsche. Automatische Erstellung hochwertiger HTML-Beschreibungen aus Text und Bildern. Volle Fulfillment-Schnittstelle für Orders, Stocks, Preise und Refunds.',
          tags: ['AI', 'Übersetzung', 'Fulfillment', 'Dropshipping'],
        },
      ],
    },
    agents: {
      label: 'KI-Agenten',
      title: 'Agent-Szenarien.',
      titleFaded: 'Anwendungsfälle.',
      description:
        'Beispiele, wie unsere KI-Agenten in Commerce-Prozessen eingesetzt werden — von Support bis Finance.',
      cards: [
        {
          title: 'Support Automation Agent',
          description:
            'Automatisiert Standardanfragen im Kundenservice und skaliert Ticketvolumen kontrolliert.',
          bullets: [
            'Klassifikation und Routing eingehender Fälle',
            'Standard-Workflows für Storno, Retoure und Statusanfragen',
            'Eskalation bei Unsicherheit',
          ],
        },
        {
          title: 'Marketplace Operations Agent',
          description:
            'Überwacht Marktplatzprozesse, erkennt Ausnahmen und steuert operative Reaktionen.',
          bullets: [
            'Monitoring von Beständen und Preisabweichungen',
            'Vendor- und Plattformprozesse absichern',
            'Früherkennung fehlerhafter Prozessketten',
          ],
        },
        {
          title: 'Content & Product Data Agent',
          description:
            'Erzeugt und validiert strukturierte Produktdaten für Shops und Marktplätze.',
          bullets: [
            'Generierung strukturierter Produktbeschreibungen',
            'Konsistenz-Checks über Attribute und Medien',
            'Vorvalidierung vor Veröffentlichung',
          ],
        },
        {
          title: 'Finance & Reconciliation Agent',
          description: 'Automatisiert Abgleich, Gebührenprüfung und Aufbereitung von Abweichungen.',
          bullets: [
            'Reconciliation über ERP- und Plattformdaten',
            'Markierung von Gebühren- und Settlement-Differenzen',
            'Nachvollziehbarer Übergabestatus an Finance',
          ],
        },
      ],
    },
    connectors: {
      label: 'Fertige Konnektoren',
      title: 'Schneller starten mit bestehenden Integrationen',
      description:
        'Denqfabrik bietet fertige Konnektoren für gängige Commerce-Plattformen — von Marktplätzen über Shop-Systeme bis zu ERP und Fulfillment. Kein Kaltstart, sondern produktionserprobte Bausteine.',
      button: 'Use-Case-Workshop anfragen',
    },
  },
  useCaseDetail: {
    kicker: 'Use Case',
    includedLabel: 'Inklusive',
    includedTitle: 'Das bekommst du out-of-the-box',
    resultsLabel: 'Ergebnisse',
    resultsTitle: 'Was sich konkret ändert',
    cta: {
      title: 'Diesen Use Case in deinem Setup bewerten',
      description:
        'Wir prüfen gemeinsam Datenlage, Systemlandschaft und Integrationsaufwand — und starten in wenigen Wochen.',
      buttonLabel: 'Use Case besprechen',
    },
    cases: {
      'fulfillment-hub': {
        title: 'Fulfillment Integrationshub',
        subtitle:
          'Zentrales Hub für die Anbindung neuer Fulfillment-Kunden — mit einheitlicher API, automatisiertem Onboarding und Echtzeit-Dashboard für Bestände, Aufträge und SLA-Einhaltung.',
        stats: [
          { value: '3h', label: 'Kunden-Onboarding' },
          { value: '50+', label: 'Angebundene Kunden' },
          { value: 'Echtzeit', label: 'Dashboard & Monitoring' },
        ],
        sections: [
          {
            title: 'Unified API Layer',
            description:
              'Eine einheitliche Schnittstelle für alle Fulfillment-Kunden — unabhängig davon, welches System der Kunde nutzt.',
            bullets: [
              'Standardisierte Endpunkte für Orders, Stocks & Returns',
              'Automatische Datenvalidierung und Fehlermeldung',
              'Versionierte API mit klarer Dokumentation',
            ],
          },
          {
            title: 'Automatisiertes Onboarding',
            description:
              'Neue Kunden werden in Stunden statt Wochen angebunden. Konfiguration statt Custom-Entwicklung für jeden Einzelfall.',
            bullets: [
              'Self-Service Konfiguration der Daten-Mappings',
              'Automatische Testläufe und Validierung',
              'Guided Setup mit Fortschrittsanzeige',
            ],
          },
          {
            title: 'Operations Dashboard',
            description:
              'Echtzeit-Überblick über alle Kunden, Bestände, Aufträge und SLA-Einhaltung — an einem Ort.',
          },
        ],
        included: [
          'Vollständige API-Dokumentation und SDK',
          'Echtzeit-Dashboard mit Kunden-Übersicht',
          'SLA-Monitoring und Alerting',
          'Automatisierte Onboarding-Pipeline',
          'Hosting und Betrieb (im Retainer)',
          'Error-Handling mit Retry-Logik und Fallbacks',
        ],
        results: [
          {
            metric: '3 Stunden statt 2 Wochen Onboarding',
            description:
              'Neue Fulfillment-Kunden sind in wenigen Stunden produktiv angebunden — inklusive Testlauf.',
          },
          {
            metric: 'Echtzeit-Transparenz für alle Beteiligten',
            description:
              'Kunden und Operations sehen jederzeit den Status von Beständen, Aufträgen und Retouren.',
          },
          {
            metric: 'Skalierbar ohne Mehraufwand',
            description:
              'Jeder neue Kunde nutzt dieselbe Infrastruktur. Kein zusätzlicher Entwicklungsaufwand pro Anbindung.',
          },
        ],
      },
      repricer: {
        title: 'Multi-Layer Repricer',
        subtitle:
          'Intelligentes Repricing-System mit vier Datenschichten — berücksichtigt Fulfillment-Kapazitäten, Verkaufsdaten, interne Regeln und Konkurrenzpreise gleichzeitig für maximale Marge.',
        stats: [
          { value: '4', label: 'Datenschichten' },
          { value: 'Custom', label: 'Regelwerk' },
          { value: 'Echtzeit', label: 'Preisanpassung' },
        ],
        sections: [
          {
            title: 'Vier-Schichten-Architektur',
            description:
              'Statt eindimensionalem Preisvergleich arbeitet der Repricer mit vier unabhängigen Datenschichten, die gewichtet zusammenfließen.',
            bullets: [
              'Schicht 1: Fulfillment-Kapazitäten und Lagerbestand',
              'Schicht 2: Produktbezogene Verkaufs- und Abverkaufsdaten',
              'Schicht 3: Interne Preisregeln, Mindestmargen und Sperren',
              'Schicht 4: Konkurrenzpreise und Marktplatz-Dynamik',
            ],
          },
          {
            title: 'Regelbasierte Preissteuerung',
            description:
              'Dein Team definiert Leitplanken — der Repricer optimiert innerhalb dieser Grenzen automatisch.',
            bullets: [
              'Mindestmargen und Preisuntergrenzen',
              'Kategorie- und saisonspezifische Regeln',
              'Manuelle Overrides für Sonderfälle',
            ],
          },
          {
            title: 'Wettbewerbsanalyse',
            description:
              'Konkurrenzpreise werden kontinuierlich erfasst und als Datenschicht in die Preisentscheidung einbezogen.',
          },
        ],
        included: [
          'Vollständig custom-designte Pricing-Engine',
          'Dashboard mit Margen- und Preisübersicht',
          'Regel-Editor für dein Team',
          'Konkurrenzpreis-Monitoring',
          'Hosting und Betrieb (im Retainer)',
          'Audit-Trail für jede Preisänderung',
        ],
        results: [
          {
            metric: 'Höhere Margen bei Wettbewerbsfähigkeit',
            description:
              'Der Repricer findet den optimalen Preis zwischen maximaler Marge und Konkurrenzfähigkeit — automatisch.',
          },
          {
            metric: 'Lagerbestand fließt in die Preisentscheidung',
            description:
              'Überhänge werden schneller abverkauft, knappe Ware wird automatisch höher bepreist.',
          },
          {
            metric: 'Volle Kontrolle für dein Team',
            description:
              'Regeln, Sperren und Overrides geben deinem Team jederzeit die Hoheit über die Preisgestaltung.',
          },
        ],
      },
      'integration-hub': {
        title: 'E-Commerce Integrationshub',
        subtitle:
          'Alle Marktplätze an einem Ort — Refunds, Orders, Stocks über eine einheitliche Schnittstelle. Mit hohen Sicherheitsstandards, Unit und E2E Tests, und neuen Marktplätzen in Tagen statt Monaten.',
        stats: [
          { value: 'Tage', label: 'Neue Marktplätze' },
          { value: '100%', label: 'Test-Coverage' },
          { value: 'Early Access', label: 'Vor den Großen' },
        ],
        sections: [
          {
            title: 'Einheitliche Marktplatz-Schnittstelle',
            description:
              'Egal ob Amazon, eBay, OTTO oder Kaufland — alle Plattformen sprechen über eine API mit deinem System.',
            bullets: [
              'Normalisierte Datenmodelle für Orders, Stocks & Refunds',
              'Plattform-spezifische Adapter hinter einheitlicher API',
              'Event-basierte Synchronisation in Echtzeit',
            ],
          },
          {
            title: 'Qualitätssicherung & Testing',
            description:
              'Jeder Konnektor wird mit Unit und E2E Tests abgesichert — keine Überraschungen in Produktion.',
            bullets: [
              'Unit Tests für jede Business-Logik',
              'E2E Tests gegen echte Sandbox-Umgebungen',
              'Automatisierte Regression bei API-Änderungen',
            ],
          },
          {
            title: 'Schnelle Marktplatz-Anbindung',
            description:
              'Neue Plattformen werden in Tagen integriert — oft bevor große Connector-Anbieter überhaupt eine Schnittstelle bereitstellen.',
          },
        ],
        included: [
          'Modulare Konnektor-Bibliothek',
          'Vollständige Test-Suite (Unit + E2E)',
          'Echtzeit-Synchronisation und Event-Queue',
          'Monitoring und Health-Checks pro Plattform',
          'Hosting und Betrieb (im Retainer)',
          'Dokumentierte API mit Versionierung',
        ],
        results: [
          {
            metric: 'Tage statt Monate für neue Marktplätze',
            description:
              'Neue Plattformen sind in wenigen Tagen produktiv angebunden — inklusive Tests und Monitoring.',
          },
          {
            metric: 'Keine Ausfälle durch getestete Konnektoren',
            description:
              'Jede Schnittstelle wird mit Unit und E2E Tests abgesichert, bevor sie live geht.',
          },
          {
            metric: 'Early-Access-Vorteil',
            description:
              'Du bist auf neuen Marktplätzen aktiv, bevor deine Konkurrenz überhaupt eine Anbindung hat.',
          },
        ],
      },
      'dropship-ai': {
        title: 'AI-Dropshipping-Anbindung',
        subtitle:
          'Anbindung eines niederländischen Partners mit AI-Übersetzung der Produkttexte ins Deutsche, automatischer HTML-Beschreibungserstellung und voller Fulfillment-Schnittstelle.',
        stats: [
          { value: 'NL → DE', label: 'AI-Übersetzung' },
          { value: 'Auto', label: 'HTML-Generierung' },
          { value: 'Voll', label: 'Fulfillment-Anbindung' },
        ],
        sections: [
          {
            title: 'AI-gestützte Übersetzung',
            description:
              'Produkttexte werden automatisch vom Niederländischen ins Deutsche übersetzt — mit Kontext und Commerce-Terminologie.',
            bullets: [
              'Kontextbezogene Übersetzung statt Wort-für-Wort',
              'Commerce-spezifisches Vokabular und Tonalität',
              'Batch-Verarbeitung für große Kataloge',
            ],
          },
          {
            title: 'Automatische Content-Erstellung',
            description:
              'Aus übersetzten Texten und Produktbildern werden hochwertige HTML-Beschreibungen generiert — publishfertig für deutsche Marktplätze.',
            bullets: [
              'HTML-Templates mit responsivem Design',
              'Bildintegration und Medien-Optimierung',
              'Formatvorgaben pro Zielkanal',
            ],
          },
          {
            title: 'Volle Fulfillment-Schnittstelle',
            description:
              'Orders, Stocks, Preise und Refunds laufen automatisiert zwischen dir und dem niederländischen Partner.',
          },
        ],
        included: [
          'AI-Übersetzungs-Pipeline (NL → DE)',
          'HTML-Content-Generator mit Templates',
          'Fulfillment-API für Orders, Stocks, Preise, Refunds',
          'Batch-Import für große Produktkataloge',
          'Hosting und Betrieb (im Retainer)',
          'Qualitätsprüfung der Übersetzungen',
        ],
        results: [
          {
            metric: 'Tausende Produkte automatisch übersetzt',
            description:
              'Der gesamte niederländische Katalog wird automatisch ins Deutsche übertragen — ohne manuelles Übersetzen.',
          },
          {
            metric: 'Publishfertige HTML-Beschreibungen',
            description:
              'Hochwertige Produktseiten werden automatisch aus Text und Bildern generiert.',
          },
          {
            metric: 'Nahtlose Prozesskette zum Partner',
            description:
              'Orders, Bestände und Retouren synchronisieren sich automatisch — keine manuelle Abstimmung.',
          },
        ],
      },
    },
  },
  solutions: {
    meta: {
      title: 'Plattformen & Integrationen',
      description:
        'Von ERP über Shop-Systeme bis zu Marktplätzen — stabile Schnittstellen, die in Produktion laufen.',
    },
    hero: {
      label: 'Plattformen & Integrationen',
      title: 'Wir integrieren Ihr Commerce-Ökosystem.',
      subtitle:
        'Von ERP über Shop-Systeme bis zu Marktplätzen — stabile Schnittstellen, die in Produktion laufen.',
    },
    categoriesSection: {
      title: 'Plattformen, die wir anbinden',
      description: 'Vier Kategorien, ein Ziel: Ihre Systeme sprechen dieselbe Sprache.',
    },
    categories: [
      {
        title: 'ERP-Systeme',
        tags: ['SAP', 'Odoo', 'weclapp', 'Xentral', 'JTL', 'Lexware', 'Custom ERPs'],
        description:
          'Anbindung von ERP-Daten und -Prozessen als Grundlage für Automation und Agenten.',
      },
      {
        title: 'Marktplätze',
        tags: ['Amazon', 'eBay', 'OTTO', 'Kaufland', 'Mirakl', 'CHECK24', 'idealo'],
        description: 'Listing, Order-Management und EDI-Compliance für alle großen Marktplätze.',
      },
      {
        title: 'Shop-Systeme',
        tags: ['Shopify', 'Shopware', 'WooCommerce', 'plentymarkets'],
        description: 'Nahtlose Integration in Shop-Plattformen für End-to-End Commerce-Workflows.',
      },
      {
        title: 'CRM & Custom',
        tags: [
          'HubSpot',
          'Salesforce',
          'Pipedrive',
          'Microsoft Dynamics 365',
          'Billbee',
          'Afterbuy',
          'ITscope',
        ],
        description: 'CRM-Anbindung und proprietäre Systeme mit klarer Vertragslogik.',
      },
    ],
    processSection: {
      title: 'Was eine Integration umfasst',
      description:
        'Jede Anbindung durchläuft vier Phasen — von der Anforderungsanalyse bis zur betriebsfertigen Übergabe.',
    },
    processSteps: [
      {
        step: '01',
        title: 'Analyse & Scope-Definition',
        description:
          'Bestandsaufnahme Ihrer Systemlandschaft, Priorisierung der Schnittstellen und klare Abgrenzung des Integrationsumfangs.',
      },
      {
        step: '02',
        title: 'API-Design & Implementierung',
        description:
          'Robuste Schnittstellenarchitektur mit Fehlerbehandlung, Retry-Logik und idempotenten Operationen.',
      },
      {
        step: '03',
        title: 'Testing & Monitoring-Setup',
        description:
          'End-to-End-Tests, Lasttests und Einrichtung von Alerting und Health-Checks für den Produktivbetrieb.',
      },
      {
        step: '04',
        title: 'Dokumentation & Übergabe',
        description:
          'Technische Dokumentation, Runbooks und strukturiertes Onboarding für Ihr Team.',
      },
    ],
    scriptButler: {
      title: 'Bereit, Ihre Systeme zu verbinden?',
      description:
        '30 Minuten, kostenlos und unverbindlich. Wir klären Ihre Fragen und geben eine erste Einschätzung.',
      button: 'Erstgespräch vereinbaren',
    },
    cta: {
      title: 'Integrationslandschaft gemeinsam planen',
      description: 'Wir priorisieren mit Ihnen die Schnittstellen, die den größten Hebel liefern.',
      button: 'Integrationsgespräch starten',
    },
  },
  solutionDetail: {
    kicker: 'Solutions Detail',
    cta: {
      title: 'Diese Integration in deinem Stack bewerten',
      description:
        'Wir prüfen Architektur, Abhängigkeiten und Sicherheitsanforderungen für eine stabile Umsetzung.',
      buttonLabel: 'Kontakt',
    },
    cases: {
      'erp-systeme': {
        title: 'ERP Systeme',
        subtitle:
          'ERP ist der Kern operativer Wahrheit. Agenten greifen strukturiert auf Bestell-, Bestands- und Finanzkontext zu.',
        sections: [
          {
            title: 'Daten- und Prozesskopplung',
            description:
              'Anbindung zentraler ERP-Objekte mit validierten Mapping- und Sync-Regeln.',
          },
          {
            title: 'Transaktionssicherheit',
            description:
              'Idempotente Ausführung, Retry-Strategien und Fehlerbehandlung für stabile Prozessketten.',
          },
          {
            title: 'Auditierbarkeit',
            description:
              'Vollständige Nachvollziehbarkeit für Agentenentscheidungen auf ERP-Basis.',
          },
        ],
      },
      'marktplatz-integrationen': {
        title: 'Marktplatz Integrationen',
        subtitle:
          'Wir verbinden operative Marktplatzprozesse mit internen Systemen für zuverlässige Automatisierung.',
        sections: [
          {
            title: 'Multi-Channel Betrieb',
            description:
              'Einheitliche Steuerung von Bestand, Preisen, Orders und Ausnahmen über Plattformgrenzen hinweg.',
          },
          {
            title: 'Vendor & Operations Flows',
            description: 'Abbildung marktplatzspezifischer Logik ohne Bruch in internen Prozessen.',
          },
          {
            title: 'Monitoring und Recovery',
            description:
              'Früherkennung fehlerhafter Flows und strukturierte Wiederanlaufstrategien.',
          },
        ],
      },
      'commerce-plattformen': {
        title: 'Commerce Plattformen',
        subtitle:
          'Shop-Systeme und Commerce-Plattformen werden zu stabilen Datenquellen und Aktionszielen für Agenten.',
        sections: [
          {
            title: 'Produkt- und Katalogprozesse',
            description: 'Integration von Produktdaten, Variantenlogik und Content-Flows.',
          },
          {
            title: 'Order- und Serviceprozesse',
            description:
              'Anbindung von Bestellung, Versand, Retoure und Servicefällen mit konsistentem Statusmodell.',
          },
          {
            title: 'Skalierbare Automation',
            description: 'Schrittweise Automatisierung mit klaren Grenzen und Human-in-the-Loop.',
          },
        ],
      },
      'custom-integrationen': {
        title: 'Custom Integrationen',
        subtitle:
          'Für proprietäre Systeme bauen wir belastbare API-Layer mit stabiler Vertrags- und Fehlermanagementlogik.',
        sections: [
          {
            title: 'API-Design und Verträge',
            description:
              'Klare Datenverträge, Versionierung und robuste Schnittstellenarchitektur.',
          },
          {
            title: 'Sicherheits- und Zugriffskontrolle',
            description:
              'Berechtigungen, Schlüsselmanagement und kontrollierter Zugriff auf sensible Endpunkte.',
          },
          {
            title: 'Betriebsfähigkeit',
            description:
              'Monitoring, Logging und Wartbarkeit als feste Bestandteile der Integration.',
          },
        ],
      },
    },
  },
  amazonSpApi: {
    meta: {
      title: 'Amazon SP-API Integrationen',
      description:
        'Professionelle Amazon SP-API Integrationen für Vendor und Seller — sicher, betriebsreif und mit klarer Architektur.',
    },
    hero: {
      label: 'Amazon Developer',
      title: 'Amazon SP-API',
      titleHighlight: 'Integrationen.',
      subtitle:
        'Wir implementieren robuste SP-API-Prozesse für Seller und Vendor — von Datenabgleich und Monitoring bis zur operativen Steuerung deiner Amazon-Kanäle.',
    },
    capabilities: {
      label: 'Leistungen',
      title: 'Was wir umsetzen',
      cards: [
        {
          title: 'SP-API Anbindung',
          description:
            'Vollständige Integration der Amazon Selling Partner API mit robusten Auth-Flows, Rate-Limit-Handling und strukturiertem Datenabgleich.',
          bullets: [
            'Orders & Fulfillment API',
            'Catalog Items & Listings',
            'Reports & Analytics',
            'Notifications & Event-Bridge',
          ],
        },
        {
          title: 'Vendor Automation',
          description:
            'Automatisierte Vendor-Prozesse mit kontrollierter Fehlerbehandlung, klaren Fallbacks und Eskalationslogik.',
          bullets: [
            'Purchase Order Processing',
            'Shipment Confirmation',
            'Invoice Automation',
            'Chargebacks & Disputes',
          ],
        },
        {
          title: 'ERP-Brücke',
          description:
            'Verknüpfung von Amazon-Daten mit deinem ERP für konsistente operative Entscheidungen.',
          bullets: [
            'Bestands-Synchronisation',
            'Auftrags-Import',
            'Rechnungs-Abgleich',
            'Stammdaten-Mapping',
          ],
        },
        {
          title: 'Gutschriften & Claims',
          description:
            'Nachvollziehbare Vorbereitung und Verarbeitung von Gutschriften inklusive Prüf- und Eskalationslogik.',
          bullets: [
            'Shortage Claims',
            'Price Claim Automation',
            'CoOp & Accruals',
            'Dispute Resolution Flows',
          ],
        },
      ],
    },
    security: {
      label: 'Sicherheit & Compliance',
      title: 'Amazon-konforme Datenverarbeitung',
      description:
        'Wir entwickeln nach Amazon Acceptable Use Policy und Data Protection Policy — mit dokumentierter Datenhaltung und klarem Zugriffskonzept.',
      points: [
        {
          title: 'Token & Auth Management',
          description:
            'Sichere Verwaltung von LWA-Tokens, automatische Rotation und verschlüsselte Speicherung.',
        },
        {
          title: 'PII-Handling',
          description:
            'Strikte Verarbeitung personenbezogener Daten nach Amazon-Vorgaben mit Löschfristen und Audit-Trail.',
        },
        {
          title: 'Rate-Limit-Architektur',
          description:
            'Adaptive Throttling-Strategien, Burst-Queues und Retry-Logik für stabile API-Nutzung.',
        },
      ],
    },
    pricing: {
      label: 'Preisstruktur',
      title: 'Transparent nach Umfang',
      description:
        'Unsere Preise richten sich nach Integrationstiefe, Komplexität und Betriebsanforderungen. Kein pauschaler Stundensatz — stattdessen klare Scope-basierte Angebote.',
      tiers: [
        {
          name: 'Starter',
          scope: 'Einzelne API-Anbindung',
          note: 'z.B. Orders-Import oder Reports-Sync',
        },
        {
          name: 'Professional',
          scope: 'Multi-API Integration',
          note: 'Vendor + Seller Flows, ERP-Brücke',
        },
        {
          name: 'Enterprise',
          scope: 'Vollständige Automation',
          note: 'End-to-End Prozesse, Monitoring, Support',
        },
      ],
    },
    cta: {
      title: 'Amazon SP-API Projekt abstimmen',
      description:
        'Wir klären mit dir Scope, Sicherheitsanforderungen und Integrationspfad für deinen konkreten Use Case.',
      button: 'Amazon-Projekt anfragen',
    },
  },
  mirakl: {
    meta: {
      title: 'Mirakl Connector — Conrad, XXXLutz, home24, OBI & mehr',
      description:
        'Mirakl-Anbindung für alle Operator-Marktplätze: Conrad, XXXLutz, home24, real, OBI, ManoMano. Ein Connector — viele Kanäle. Professionell implementiert.',
    },
    hero: {
      label: 'Marketplace Framework',
      title: 'Mirakl',
      titleHighlight: 'Connector.',
      subtitle:
        'Mirakl ist das Marktplatz-Framework hinter Conrad, XXXLutz, home24, real, OBI und ManoMano. Ein einziger Connector erschließt alle Mirakl-betriebenen Kanäle — ohne Neuimplementierung pro Operator.',
    },
    capabilities: {
      label: 'Leistungen',
      title: 'Was wir umsetzen',
      cards: [
        {
          title: 'Mirakl API Anbindung',
          description:
            'Vollständige Integration über die Mirakl Operator API — OAuth-Flow, Webhook-Handling und strukturierter Datenabgleich für alle verbundenen Kanäle.',
          bullets: ['Orders API', 'Products & Offers Sync', 'Shipping & Tracking', 'Returns & Refunds'],
        },
        {
          title: 'Multi-Channel Konfiguration',
          description:
            'Jeder Mirakl-Operator läuft im eigenen konfigurierbaren Kontext — kein Channel-Bleeding, klare Fehlerzuordnung, kanalspezifisches Mapping.',
          bullets: ['Conrad Electronics', 'XXXLutz / Möbelix', 'home24 / real', 'OBI / ManoMano'],
        },
        {
          title: 'Bestands- & Preissync',
          description:
            'Realtime-Bestandsabgleich mit konfigurierbaren Puffern, kanalspezifische Preisregeln und automatische Delistung bei Nullbestand.',
          bullets: ['Bestandspuffer pro Kanal', 'Kanalspezifische Preise', 'Verfügbarkeitsmanagement', 'Automatische Delistung'],
        },
        {
          title: 'Shop-System-Integration',
          description:
            'Nahtlose Verknüpfung mit Shopware, Shopify, JTL, plentymarkets und anderen Commerce-Systemen über standardisierte Schnittstellen.',
          bullets: ['Shopware & Shopify', 'JTL-Wawi', 'plentymarkets', 'Eigenentwicklungen & ERP'],
        },
      ],
    },
    architecture: {
      label: 'Architektur',
      title: 'Skalierbar von einem auf alle Kanäle',
      description:
        'Ein Mirakl-Connector — viele Operator-Instanzen. Die Architektur wächst mit dir: starte mit einem Kanal, erweitere auf alle Mirakl-betriebenen Marktplätze ohne Neuimplementierung.',
      points: [
        {
          title: 'Operator-Isolation',
          description:
            'Jede Mirakl-Instanz läuft in einem eigenen konfigurierbaren Kontext. Kein Channel-Bleeding, klare Fehlerortung, kanalspezifische Konfiguration.',
        },
        {
          title: 'Dry-Run-Default',
          description:
            'Alle Sync-Operationen starten im Dry-Run-Modus. Produktivdaten werden erst nach expliziter Freigabe berührt — kein versehentliches Überschreiben.',
        },
        {
          title: 'Monitoring & Alerting',
          description:
            'Automatische Alerts bei API-Fehlern, ausgebliebenen Sync-Läufen oder Datenanomalien — mit Job-Historie und detaillierten Fehlerprotokollen.',
        },
      ],
    },
    pricing: {
      label: 'Preisstruktur',
      title: 'Transparent nach Umfang',
      description:
        'Preis richtet sich nach Kanalanzahl, Integrationstiefe und Betriebsanforderungen. Klare Scope-Angebote — kein pauschaler Stundensatz.',
      tiers: [
        {
          name: 'Starter',
          scope: 'Ein Mirakl-Operator',
          note: 'z.B. Conrad oder ManoMano',
        },
        {
          name: 'Professional',
          scope: 'Bis zu 5 Operators',
          note: 'Multi-Channel mit ERP-Anbindung',
        },
        {
          name: 'Enterprise',
          scope: 'Alle Operators + Automation',
          note: 'Monitoring, Betrieb, SLA',
        },
      ],
    },
    cta: {
      title: 'Mirakl-Anbindung anfragen',
      description:
        'Wir klären mit dir Kanalauswahl, Integrationstiefe und technischen Aufwand — und liefern ein konkretes Angebot.',
      button: 'Mirakl-Projekt anfragen',
    },
  },
  woocommerce: {
    meta: {
      title: 'WooCommerce anbinden — Marktplätze, Shops und ERP',
      description:
        'WooCommerce an Marktplätze, Shops und ERP anbinden — gebaut, getestet und betrieben. Bestellungen, Bestände und Preise laufen automatisch. Kein manuelles Nachpflegen.',
    },
    hero: {
      label: 'WooCommerce Integration',
      title: 'WooCommerce',
      titleHighlight: 'anbinden.',
      subtitle:
        'Wir binden WooCommerce an Marktplätze, Shops und ERP an — gebaut, getestet und betrieben. Bestellungen, Bestände und Preise laufen automatisch zwischen Ihren Systemen. Kein manuelles Nachpflegen, kein Baukasten.',
    },
    strip: {
      label: 'Typische Gegenstellen',
      items: [
        'Amazon',
        'Mirakl',
        'Kaufland',
        'OTTO',
        'eBay',
        'JTL',
        'weclapp',
        'Plentymarkets',
        'Afterbuy',
      ],
    },
    capabilities: {
      label: 'Leistungen',
      title: 'Was wir umsetzen',
      cards: [
        {
          title: 'Bestellungen & Abwicklung',
          description:
            'Bestellungen aus Marktplätzen und Shops landen in WooCommerce, Status und Sendungsdaten laufen zurück — inklusive Teillieferungen und Retouren.',
          bullets: [
            'Bestellimport aus Marktplätzen',
            'Status- und Tracking-Rückmeldung',
            'Teillieferungen & Nachlieferungen',
            'Retouren und Gutschriften',
          ],
        },
        {
          title: 'Bestände & Preise',
          description:
            'Ein Bestand, viele Kanäle: Abgleich mit konfigurierbaren Puffern, kanalspezifische Preisregeln und automatische Delistung bei Nullbestand.',
          bullets: [
            'Bestandspuffer pro Kanal',
            'Kanalspezifische Preise',
            'Automatische Delistung',
            'Varianten- und SKU-Mapping',
          ],
        },
        {
          title: 'ERP-Brücke',
          description:
            'WooCommerce spricht mit Ihrer Warenwirtschaft, nicht nebenher. Stammdaten, Aufträge und Belege gehen in beide Richtungen.',
          bullets: [
            'JTL-Wawi',
            'weclapp',
            'Plentymarkets & Afterbuy',
            'Buchhaltungs-Export (DATEV, lexware)',
          ],
        },
        {
          title: 'REST-API & Plugins',
          description:
            'Wir arbeiten auf der WooCommerce-REST-API und mit eigenen Plugins, wo die API nicht reicht — statt Ihren Prozess in ein fremdes Plugin zu biegen.',
          bullets: [
            'WooCommerce REST API v3',
            'Webhooks & Event-Verarbeitung',
            'Eigene Endpunkte, wo nötig',
            'Migration von Bestands-Plugins',
          ],
        },
      ],
    },
    operations: {
      label: 'Betrieb',
      title: 'Gebaut, getestet, betrieben',
      description:
        'Eine Schnittstelle ist mit dem Go-Live nicht fertig — Endpunkte ändern sich, Kanäle ändern Regeln. Deshalb gehört der Betrieb zur Lieferung.',
      points: [
        {
          title: 'Dry-Run als Standard',
          description:
            'Jeder Sync startet im Dry-Run. Produktivdaten werden erst nach ausdrücklicher Freigabe geschrieben — kein versehentliches Überschreiben von Beständen oder Preisen.',
        },
        {
          title: 'Monitoring & Alerting',
          description:
            'Alarm bei API-Fehlern, ausgebliebenen Läufen und Datenanomalien — mit Job-Historie, damit nachvollziehbar bleibt, welcher Lauf welchen Wert geschrieben hat.',
        },
        {
          title: 'Fester Ansprechpartner',
          description:
            'Wenn eine Schnittstelle bricht, brennt es im Tagesgeschäft. Sie erreichen die Person, die die Anbindung gebaut hat — nicht ein Ticket-System.',
        },
      ],
    },
    proof: {
      label: 'Belegt',
      items: [
        'Sieben Mirakl-Marktplätze laufen bei uns parallel im Tagesgeschäft (5× DE, 2× AT) — inklusive Bestands- und Preis-Sync.',
        'Für Playflip haben wir die Partnerlager-Schnittstellen in Plentymarkets gebaut: vollautomatisierte Auftragsabwicklung im Dropshipping.',
      ],
    },
    pricing: {
      label: 'Preisstruktur',
      title: 'Transparent nach Umfang',
      description:
        'Der Preis richtet sich nach Anzahl der Kanäle, Integrationstiefe und Betriebsanforderungen. Klare Scope-Angebote — kein pauschaler Stundensatz.',
      tiers: [
        {
          name: 'Starter',
          scope: 'Eine Anbindung',
          note: 'z.B. WooCommerce ↔ ein Marktplatz',
        },
        {
          name: 'Professional',
          scope: 'Mehrere Kanäle + ERP',
          note: 'Bestände, Preise, Bestellungen, Belege',
        },
        {
          name: 'Enterprise',
          scope: 'Vollständige Automation',
          note: 'End-to-End-Prozesse, Monitoring, Betrieb',
        },
      ],
    },
    cta: {
      title: 'WooCommerce-Anbindung anfragen',
      description:
        'Wir klären mit Ihnen Kanäle, Datenrichtung und Integrationstiefe — und liefern ein konkretes Angebot.',
      button: 'WooCommerce-Projekt anfragen',
    },
  },
  shopware: {
    meta: {
      title: 'Shopware anbinden — ERP, Marktplätze und Buchhaltung',
      description:
        'Shopware an weclapp, JTL, Marktplätze und Buchhaltung anbinden — gebaut, getestet und betrieben. Bestellungen, Bestände und Preise laufen automatisch. Kein manuelles Nachpflegen.',
    },
    hero: {
      label: 'Shopware Integration',
      title: 'Shopware',
      titleHighlight: 'anbinden.',
      subtitle:
        'Wir verbinden Shopware mit Ihrem ERP und Ihren Verkaufskanälen — weclapp, JTL, Plentymarkets, Amazon, Mirakl und mehr. Bestellungen, Bestände und Preise laufen automatisch. Kein manuelles Nachpflegen, kein Baukasten.',
    },
    strip: {
      label: 'Häufige Systempaare',
      items: [
        'weclapp ↔ Shopware',
        'JTL ↔ Shopware',
        'Shopware ↔ Amazon',
        'Shopware ↔ Mirakl',
        'Shopware ↔ Kaufland',
        'Shopware ↔ OTTO',
        'A.eins ↔ Shopware',
      ],
    },
    capabilities: {
      label: 'Leistungen',
      title: 'Was wir umsetzen',
      cards: [
        {
          title: 'ERP-Anbindung',
          description:
            'Die häufigste Anfrage und der Kern unserer Arbeit: Shopware und Warenwirtschaft als ein Prozess statt zwei Datenstände, die jemand von Hand ausgleicht.',
          bullets: [
            'weclapp ↔ Shopware',
            'JTL-Wawi ↔ Shopware',
            'A.eins und Plentymarkets',
            'Buchhaltungs-Export (DATEV, lexware)',
          ],
        },
        {
          title: 'Marktplatz-Anbindung',
          description:
            'Shopware bleibt Ihr Stammdaten-Zuhause, die Kanäle bekommen daraus Angebote, Bestände und Preise — und liefern Bestellungen zurück.',
          bullets: [
            'Amazon (SP-API)',
            'Mirakl-Marktplätze',
            'Kaufland, OTTO, eBay',
            'Idealo und itscope',
          ],
        },
        {
          title: 'Bestände & Preise',
          description:
            'Abgleich mit konfigurierbaren Puffern, kanalspezifischen Preisregeln und automatischer Delistung — pro Kanal getrennt konfigurierbar.',
          bullets: [
            'Bestandspuffer pro Kanal',
            'Kanalspezifische Preise',
            'Automatische Delistung',
            'Varianten- und SKU-Mapping',
          ],
        },
        {
          title: 'Shopware-6-API & Plugins',
          description:
            'Wir arbeiten auf der Admin- und Store-API von Shopware 6 und schreiben eigene Plugins, wo die API nicht reicht — keine gekaufte Blackbox im Kernprozess.',
          bullets: [
            'Admin API & Store API',
            'Sync-Plugins in eigener Hand',
            'Multi-Sales-Channel-Setups',
            'Migration von Shopware 5',
          ],
        },
      ],
    },
    operations: {
      label: 'Betrieb',
      title: 'Gebaut, getestet, betrieben',
      description:
        'Eine Schnittstelle ist mit dem Go-Live nicht fertig — Endpunkte ändern sich, Kanäle ändern Regeln. Deshalb gehört der Betrieb zur Lieferung.',
      points: [
        {
          title: 'Dry-Run als Standard',
          description:
            'Jeder Sync startet im Dry-Run. Produktivdaten werden erst nach ausdrücklicher Freigabe geschrieben — kein versehentliches Überschreiben von Beständen oder Preisen.',
        },
        {
          title: 'Monitoring & Alerting',
          description:
            'Alarm bei API-Fehlern, ausgebliebenen Läufen und Datenanomalien — mit Job-Historie, damit nachvollziehbar bleibt, welcher Lauf welchen Wert geschrieben hat.',
        },
        {
          title: 'Fester Ansprechpartner',
          description:
            'Wenn eine Schnittstelle bricht, brennt es im Tagesgeschäft. Sie erreichen die Person, die die Anbindung gebaut hat — nicht ein Ticket-System.',
        },
      ],
    },
    proof: {
      label: 'Belegt',
      items: [
        'Sieben Mirakl-Marktplätze laufen bei uns parallel im Tagesgeschäft (5× DE, 2× AT) — inklusive Bestands- und Preis-Sync.',
        'Für Playflip haben wir die Partnerlager-Schnittstellen in Plentymarkets gebaut: vollautomatisierte Auftragsabwicklung im Dropshipping.',
      ],
    },
    pricing: {
      label: 'Preisstruktur',
      title: 'Transparent nach Umfang',
      description:
        'Der Preis richtet sich nach Anzahl der Systeme, Integrationstiefe und Betriebsanforderungen. Klare Scope-Angebote — kein pauschaler Stundensatz.',
      tiers: [
        {
          name: 'Starter',
          scope: 'Ein Systempaar',
          note: 'z.B. weclapp ↔ Shopware',
        },
        {
          name: 'Professional',
          scope: 'ERP + mehrere Kanäle',
          note: 'Bestände, Preise, Bestellungen, Belege',
        },
        {
          name: 'Enterprise',
          scope: 'Vollständige Automation',
          note: 'End-to-End-Prozesse, Monitoring, Betrieb',
        },
      ],
    },
    cta: {
      title: 'Shopware-Anbindung anfragen',
      description:
        'Sagen Sie uns, welches ERP und welche Kanäle im Spiel sind — wir klären Datenrichtung und Aufwand und liefern ein konkretes Angebot.',
      button: 'Shopware-Projekt anfragen',
    },
  },
  sicherheit: {
    meta: {
      title: 'Sichere KI-Agenten by design — Sicherheitsarchitektur',
      description:
        'Wie wir sichere KI-Agenten bauen: Kontrollierte Pipeline von Eingabe bis Audit Trail. Mehrstufige Verifikation, Guardrails und EU AI Act Compliance.',
    },
    hero: {
      label: 'Security Architecture',
      titleLine1: 'Sichere KI-Agenten',
      titleLine2: 'by design.',
      subtitle:
        'Jede Entscheidung durchläuft eine kontrollierte Pipeline — von der Eingabe bis zum Audit Trail. Kein Agent geht live ohne Sicherheitsarchitektur.',
    },
    seoHeadings: [
      'Wie baut man sichere KI-Agenten?',
      'KI-Agenten Sicherheitsarchitektur für E-Commerce',
      'EU AI Act Compliance für KI-Agenten',
    ],
    cards: [
      {
        number: '01',
        title: 'Eingabe & Klassifikation',
        description:
          'Jede Anfrage wird validiert, normalisiert und klassifiziert, bevor sie in die Pipeline kommt. Fälle außerhalb trainierter Muster werden erkannt und gestoppt — nicht geraten.',
        bullets: [
          'Input-Validierung und Formatprüfung',
          'Erkennung unbekannter Anfragemuster',
          'Confidence-Schwelle erzwingt Eskalation',
        ],
      },
      {
        number: '02',
        title: 'Kontext & Regelversionen',
        description:
          'Der Agent lädt Regeln, Verträge und operative Daten zum Zeitpunkt der Entscheidung. Wenn sich Regeln zwischen Ereignis und Bearbeitung ändern, weiß der Agent, welche Version gilt.',
        bullets: [
          'Zeitpunktbezogener Entscheidungskontext',
          'Versionierte Geschäftsregeln',
          'Reproduzierbare Entscheidungsgrundlage',
        ],
      },
      {
        number: '03',
        title: 'Entscheidung & Verifikation',
        description:
          'Kritische Entscheidungen werden von mehreren unabhängigen Modellen geprüft. Bei Uneinigkeit entscheidet ein Mensch — der Agent hat bei heiklen Aktionen nie das letzte Wort.',
        bullets: [
          'Unabhängige Mehrfachprüfung',
          'Uneinigkeit triggert menschliche Prüfung',
          'Getrennte Verantwortlichkeiten pro Stufe',
        ],
      },
      {
        number: '04',
        title: 'Guardrails & Berechtigungen',
        description:
          'Jeder Agent arbeitet in klar definierten Grenzen. Berechtigungen folgen dem Least-Privilege-Prinzip, Aktionen sind begrenzt und kritische Operationen sind rückgängig machbar.',
        bullets: [
          'Minimale Berechtigungen pro Agent',
          'Aktionslimits und gesperrte Pfade',
          'Rollback-fähige Operationen',
        ],
      },
      {
        number: '05',
        title: 'Audit Trail & Compliance',
        description:
          'Jeder Schritt wird protokolliert — nicht nur was passierte, sondern warum. Entscheidungspfade dokumentieren Eingabe, Kontext, Begründung und Ergebnis.',
        bullets: [
          'Vollständiger Entscheidungslog',
          'Begründung jeder Aktion dokumentiert',
          'EU AI Act Art. 13 — Transparenzanforderungen',
        ],
      },
    ],
    cta: {
      title: 'Wie sicher ist Ihre Agentenarchitektur?',
      description:
        'Wir analysieren Ihre bestehende oder geplante KI-Pipeline und identifizieren Lücken — von Klassifikation bis Compliance.',
      button: 'Sicherheits-Check anfragen',
    },
  },
  pricingPage: {
    meta: {
      title: 'Preise — Schnittstellen ab 109 € im Monat',
      description:
        'Standard-Anbindung ab 109 € im Monat, jedes weitere Skript ab 30 €, keine Einrichtungsgebühr. Fertig für Shopify, Amazon, Mirakl, TikTok Shop und A.eins.',
    },
    hero: {
      label: 'Integrationen',
      titlePrefix: 'Eure Schnittstelle.',
      titleHighlight: 'Ab 109 € im Monat.',
      subtitle:
        'Bestand, Preise und Bestellungen automatisch synchron zwischen Shop, Marktplatz und ERP. Für Shopify, Amazon, Mirakl, TikTok Shop und A.eins steht die Anbindung bereits — live in 1–2 Wochen, sobald Zugang und Testobjekt stehen. Ein anderes System — Afterbuy, JTL, plentymarkets oder euer ERP? Bauen wir dazu: Starttermin nach Absprache — ohne Aufpreis.',
      priceLine:
        'Keine Einrichtungsgebühr. Mindestlaufzeit drei Monate ab Betriebsbeginn, danach monatlich kündbar.',
    },
    tiers: [
      {
        title: 'Standard-Anbindung',
        strapline: 'Fertige Anbindung für ein gängiges System. Läuft automatisch, im Hintergrund.',
        price: 'ab 109 €',
        period: '/ Monat',
        priceNote: 'Keine Einrichtungsgebühr',
        bullets: [
          'Fertig für Shopify, Amazon, Mirakl, TikTok Shop und A.eins — live in 1–2 Wochen, sobald Zugang und Testobjekt stehen.',
          'Neues System? Bauen wir dazu: Starttermin nach Absprache — ohne Aufpreis.',
          'Bestand, Preise oder Bestellungen laufen automatisch synchron, mehrmals täglich.',
          'Mindestlaufzeit drei Monate ab Betriebsbeginn, danach monatlich kündbar. Keine Ablöse.',
        ],
        cta: 'Anbindung anfragen',
      },
      {
        title: 'Zusatz-Skript',
        strapline:
          'Jede weitere Automatisierung auf eurer bestehenden Anbindung — einzeln gebucht, schnell dazu.',
        price: 'ab 30 €',
        period: '/ Monat je Skript',
        priceNote: 'Keine Einrichtungsgebühr',
        bullets: [
          'Läuft auf der Verbindung, die schon steht — kein neues Onboarding.',
          'Beispiele, was Kunden zuletzt gebucht haben: Versandgebühr nur beim ersten Packstück berechnen. Teilerstattungen automatisch anlegen. Bestellungen nach eigenen Regeln filtern, bevor sie ins ERP gehen.',
          'Ihr beschreibt die Regel, wir bauen und testen sie an eurem System.',
          'Sonderwünsche möglich — braucht eine Regel eigene Logik statt Standard-Konfiguration, sagen wir vorher, was das kostet.',
        ],
        cta: 'Skript anfragen',
      },
      {
        title: 'Code-Eigentum',
        strapline: 'Eure eigene Script-Butler-Instanz – auf eurer Infrastruktur.',
        price: 'ab 10.000 €',
        period: 'Festpreis',
        priceNote: 'plus Wartung',
        bullets: [
          'Eigenes Dashboard unter eurer eigenen Domain.',
          'Eigenes Repository mit dem Infrastruktur-Code von Script Butler – Laufzeit, Konnektoren und eure Flows.',
          'Läuft auf eurem eigenen Supabase- und Railway-Konto: sofort startklar statt monatelangem Aufbau.',
          'Eure eigenen KI-Agenten können Server und Code direkt befragen.',
          'Neue Skripte und Flows beauftragt ihr weiterhin bei uns. Nicht Teil des Pakets: unser Agenten-Framework und die Skills, mit denen wir Skripte erzeugen.',
        ],
        footnote: 'Für Unternehmen, die Geschwindigkeit wollen und unabhängig bleiben müssen.',
        cta: 'Angebot anfragen',
      },
    ],
    proofLine:
      'Ein Kunde synchronisiert Bestand und Preise über sieben Mirakl-Marktplätze gleichzeitig — fünf in Deutschland, zwei in Österreich, alle zwei Stunden, ohne manuellen Abgleich.',
    process: {
      title: 'Wie läuft das ab',
      steps: [
        'Ihr schickt uns Zugang zum System, das wir anbinden sollen — API-Zugangsdaten oder die nötigen Rechte, je nach System.',
        'Ihr legt ein Testobjekt an — meist eine Testbestellung, bei Bestands- oder Preis-Anbindungen ein einzelner Testartikel. Daran prüfen wir die Strecke, bevor etwas automatisch läuft. Der Grund: Ein falscher Bestand korrigiert sich beim nächsten Lauf von selbst. Eine doppelt gemeldete Bestellung nicht — das sind zwei Nachrichten an euren Käufer.',
        'Wir fahren die Strecke einmal komplett durch und bestätigen euch schriftlich, dass sie steht. Stehen Zugang und Testobjekt, seid ihr bei einem fertigen System in ein bis zwei Wochen live — bei einem neuen System zum vereinbarten Starttermin.',
      ],
    },
    cta: {
      title: 'Bereit für die Anbindung?',
      description:
        'Schickt uns kurz, welches System ihr anbinden wollt — wir melden uns mit einer Einschätzung.',
      note: 'Ein Telefonat braucht es dafür nicht. Wer lieber spricht, schreibt es einfach dazu.',
      button: 'Anbindung anfragen',
    },
    faq: {
      label: 'FAQ',
      title: 'Häufige Fragen',
      items: [
        {
          question: 'Was kostet eine Anbindung?',
          answer:
            'Eine Standard-Anbindung kostet ab 109 € im Monat — 79 € Sockel plus 30 € für die erste Automation. Jedes weitere Skript kommt ab 30 € im Monat dazu. Eine Einrichtungsgebühr gibt es nicht.',
        },
        {
          question: 'Wie lange bin ich gebunden?',
          answer:
            'Mindestlaufzeit drei Monate ab Betriebsbeginn, danach monatlich zum Monatsende kündbar. Keine Ablöse.',
        },
        {
          question: 'Wie schnell ist eine Anbindung live?',
          answer:
            'Für Shopify, Amazon, Mirakl, TikTok Shop und A.eins in 1–2 Wochen, sobald Zugang und Testobjekt stehen. Für ein neues System: Starttermin nach Absprache — ohne Aufpreis.',
        },
        {
          question: 'Was, wenn ich später noch etwas automatisieren will?',
          answer:
            'Dann kommt ein Zusatz-Skript dazu — ab 30 € im Monat, auf der Anbindung, die schon läuft. Kein neues Projekt, keine Einrichtungsgebühr.',
        },
        {
          question: 'Kann ich das auf meiner eigenen Infrastruktur betreiben?',
          answer:
            'Ja, mit Code-Eigentum: eure eigene Script-Butler-Instanz auf eurem eigenen Supabase- und Railway-Konto, ab 10.000 € Festpreis plus Wartung.',
        },
      ],
    },
  },
  integrationen: {
    meta: {
      title: 'Integrationen',
      description:
        'Wir verbinden KI-Agenten sicher mit deinen Systemen — ERP, Marktplätze, Commerce-Plattformen und CRM.',
    },
    hero: {
      label: 'Integrationen',
      titlePrefix: 'Der Bridge Layer zu deinem',
      titleHighlight: 'Geschäft.',
      subtitle:
        'Stabile Integrationen statt isolierter KI-Inseln. Wir verbinden Agenten sicher mit den Systemen, die dein Tagesgeschäft steuern.',
    },
    features: [
      {
        title: 'Fehlertoleranz',
        items: [
          'Retry- & Circuit-Breaker-Strategien',
          'Graceful Degradation',
          'Wiederanlauf nach Ausfall',
        ],
      },
      {
        title: 'Systemübergreifend',
        items: [
          'Cross-System Prozessketten',
          'Einheitliche Datenmodelle',
          'Event-basierte Synchronisation',
        ],
      },
      {
        title: 'Betriebsreif',
        items: ['Monitoring & Alerting', 'Health Checks', 'Keine Demo-Integrationen'],
      },
      {
        title: 'Custom APIs',
        items: ['Klare Vertragslogik', 'Versionierung & Docs', 'Für proprietäre Systeme'],
      },
    ],
    platforms: {
      label: 'Plattformen',
      title: '50+ Integrationen.',
      titleFaded: '4 Kategorien.',
      description: 'Hover über eine Kategorie um alle unterstützten Plattformen zu sehen.',
    },
    cta: {
      title: 'Integrationslandschaft gemeinsam planen',
      description:
        'Wir priorisieren mit dir die Schnittstellen, die den größten Hebel für sichere Agenten liefern.',
      button: 'Integrationsgespräch starten',
    },
  },
  platformGrid: {
    categories: {
      erp: 'ERP',
      marketplaces: 'Marktplätze',
      commerce: 'Commerce',
      crm: 'CRM',
    },
  },
  funnel: {
    bookCall: 'Termin buchen',
    flows: {
      stock: {
        label: 'Bestandsabgleich',
        oneLiner: 'Verfügbarkeiten laufen automatisch in beide Richtungen.',
      },
      price: { label: 'Preisabgleich', oneLiner: 'Preise und Aktionen bleiben synchron.' },
      orders: {
        label: 'Bestellimport',
        oneLiner: 'Bestellungen landen vollständig im führenden System.',
      },
      tracking: {
        label: 'Versand & Tracking',
        oneLiner: 'Sendungsnummern gehen zurück an den Kanal.',
      },
      returns: {
        label: 'Retouren & Gutschriften',
        oneLiner: 'Retouren und Erstattungen werden sauber zurückgeschrieben.',
      },
      supplier: {
        label: 'Lieferantenbestellung',
        oneLiner: 'Nachschub wird automatisch beim Lieferanten ausgelöst.',
      },
      receipt: {
        label: 'Wareneingang',
        oneLiner: 'Zugänge werden gebucht, ohne dass jemand tippt.',
      },
      catalog: {
        label: 'Artikelanlage & Listings',
        oneLiner: 'Neue Artikel landen samt Attributen im Kanal.',
      },
      invoices: {
        label: 'Rechnungen & Belege',
        oneLiner: 'Belege werden erzeugt, korrigiert und zugeordnet.',
      },
      tickets: {
        label: 'Tickets & Kundenservice',
        oneLiner: 'Anfragen laufen dort auf, wo Ihr Team ohnehin arbeitet.',
      },
    },
    scaleOptions: {
      daily: 'einmal täglich',
      hourly: 'stündlich',
      fast: 'alle 5 Minuten',
      unknown: 'weiß ich noch nicht',
    },
    labels: {
      source: {
        title: 'Welches System führt bei Ihnen?',
        hint: 'Shop oder Warenwirtschaft — das System, in dem Ihre Artikel und Bestellungen leben.',
        customLabel: 'Nicht dabei? Einfach eintragen.',
        customPlaceholder: 'z. B. Eigenentwicklung, SAP, Xentral …',
      },
      target: {
        title: 'Womit soll es verbunden werden?',
        hint: 'Mehrfachauswahl möglich.',
        customLabel: 'Anderer Kanal? Einfach eintragen.',
        customPlaceholder: 'z. B. ein Mirakl-Marktplatz, ein Großhändler, eine eigene API …',
      },
      flows: {
        title: 'Was soll automatisch laufen?',
        hint: 'Mehrfachauswahl. Alles ist anklickbar — auch was wir nicht als Standard führen.',
        customLabel: 'Sonderfall? Beschreiben oder Spec einfügen.',
        customPlaceholder: 'Gerne auch als Text aus Ihrem KI-Tool — wir lesen das.',
      },
      scale: {
        title: 'Wie oft soll das laufen?',
        hint: 'Der Takt bestimmt den Aufwand — nicht die Zahl Ihrer Bestellungen.',
      },
      back: 'Zurück',
      continueLabel: 'Weiter',
      stepIndicator: 'Schritt {current} von {total}',
      result: {
        title: 'Ihr Projekt',
        sourceLabel: 'System',
        targetsLabel: 'Kanäle',
        flowsLabel: 'Abläufe',
        scaleLabel: 'Takt',
        notesLabel: 'Notizen',
        restart: 'Auswahl zurücksetzen',
      },
      close: 'Schließen',
      nonStandardBadge: 'kein Standard',
      nonStandardHint:
        'Gestrichelt heißt: für diese Kombination nicht unser Standard. Trotzdem anklickbar — unsere Liste ist nicht allwissend, und fast alles lässt sich bauen.',
    },
    outcome: {
      headline: 'So arbeiten wir.',
      subline:
        'Fertige Anbindungen in 1 – 2 Wochen live, ein neues System mit Starttermin nach Absprache und ohne Aufpreis — danach überwacht im Betrieb.',
      build:
        'Wir bauen {flows} über {channels} und testen gegen Ihre echten Daten — im Trockenlauf, bevor irgendetwas rausgeht.',
      flowsOne: 'den Ablauf',
      flowsMany: 'die {count} Abläufe',
      channelsOne: 'einen Kanal',
      channelsMany: '{count} Kanäle',
      manualTest:
        'Wir testen manuell, und ein Mensch begleitet Ihre erste echte Bestellung end-to-end durch die Fulfillment-Strecke.',
      monitored: 'Danach läuft es überwacht: Fehler sehen wir, bevor Sie sie sehen.',
      directDeveloper: 'Sie sprechen direkt mit dem Entwickler — nicht mit dem Vertrieb.',
      fastCadence: 'Fünf-Minuten-Takt prüfen wir vorab — nicht jede Schnittstelle trägt ihn.',
      nonStandard: 'Sonderfall dabei: {flows} — machbar, wir schauen kurz gemeinsam drauf.',
    },
    contact: {
      title: 'Wohin dürfen wir antworten?',
      hint: 'Wir melden uns innerhalb von 24 Stunden — meist deutlich schneller.',
      nameLabel: 'Ihr Name',
      emailLabel: 'E-Mail',
      companyLabel: 'Firma',
      submitLabel: 'Anfrage senden',
      submittingLabel: 'Wird gesendet …',
      successTitle: 'Angekommen.',
      successBody:
        'Wir haben Ihre Auswahl samt Notizen erhalten und melden uns innerhalb von 24 Stunden bei Ihnen.',
      errorBody:
        'Das hat gerade nicht geklappt. Bitte noch einmal versuchen — oder direkt einen Termin buchen.',
    },
    briefing: {
      system: 'System',
      channels: 'Kanäle',
      flows: 'Abläufe',
      nonStandard: 'Davon Sonderfall',
      cadence: 'Takt',
      notes: 'Anmerkungen',
    },
  },
};
